import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Check, Share2, RefreshCw } from "lucide-react";
import { http, fmtErr } from "../lib/api";

// Read ?ref=XYZ once (module scope) so every WaitlistForm on the page uses the same code.
function readRefFromUrl() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  return ref ? ref.trim().toUpperCase() : null;
}

export function WaitlistForm({ interest = "retail", compact = false, headline, subhead }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { referral_code, position, referrals_count, boost_per_referral, total_on_list }
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const referredBy = readRefFromUrl();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/waitlist", {
        email, name, interest, notes,
        referred_by: referredBy || undefined,
      });
      setResult(data);
      toast.success(data.already_on_list
        ? "You're already on the list — here's your share link!"
        : "You're on the list! Share your link to jump the queue.");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  };

  const refresh = async () => {
    if (!result?.referral_code) return;
    setRefreshing(true);
    try {
      const { data } = await http.get(`/waitlist/status?code=${result.referral_code}`);
      setResult((prev) => ({ ...prev, ...data }));
    } catch { /* silent */ }
    finally { setRefreshing(false); }
  };

  const shareUrl = result?.referral_code
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${result.referral_code}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — long-press to copy manually."); }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copy(); return; }
    try {
      await navigator.share({
        title: "Alekas Treats — get on the list",
        text: `I just joined the Alekas Treats waitlist for real-deal chamoy candy. Use my link so we both jump the queue:`,
        url: shareUrl,
      });
    } catch { /* user cancelled */ }
  };

  const input = "w-full bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F]";

  if (result) {
    const boost = result.boost_per_referral || 5;
    return (
      <div className="bg-[#7CB342]/15 border-2 border-[#7CB342] p-6 space-y-4" data-testid={`waitlist-success-${interest}`}>
        <div className="font-heading text-2xl uppercase tracking-wide flex items-center gap-2">
          <Sparkles size={22} className="text-[#7CB342]" /> You&apos;re on the list
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-[#E5D8C1] p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#5C5A56]">Your spot</div>
            <div className="font-heading text-4xl text-[#D32F2F]" data-testid={`waitlist-position-${interest}`}>
              #{result.position}
            </div>
            <div className="text-[10px] text-[#5C5A56] mt-1">of {result.total_on_list} on the list</div>
          </div>
          <div className="bg-white border border-[#E5D8C1] p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#5C5A56]">Friends referred</div>
            <div className="font-heading text-4xl text-[#7CB342]" data-testid={`waitlist-referrals-${interest}`}>
              {result.referrals_count}
            </div>
            <div className="text-[10px] text-[#5C5A56] mt-1">jump {boost} spots per friend</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5D8C1] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#5C5A56] mb-1">Your share link</div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 text-sm font-mono bg-transparent outline-none truncate"
              onFocus={(e) => e.target.select()}
              data-testid={`waitlist-share-url-${interest}`}
            />
            <button
              onClick={copy}
              className="shrink-0 bg-[#1A1A1A] text-white px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1 hover:bg-[#D32F2F]"
              data-testid={`waitlist-copy-${interest}`}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={nativeShare}
            className="flex-1 bg-[#D32F2F] hover:bg-[#B71C1C] text-white py-3 uppercase tracking-widest text-sm font-heading flex items-center justify-center gap-2"
            data-testid={`waitlist-share-${interest}`}
          >
            <Share2 size={14} /> Share &amp; jump the queue
          </button>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="bg-white border border-[#E5D8C1] px-3 hover:border-[#D32F2F] disabled:opacity-50"
            title="Refresh count"
            data-testid={`waitlist-refresh-${interest}`}
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>

        <p className="text-xs text-[#5C5A56] text-center">
          Every friend who signs up with your link bumps you <b>{boost} spots</b> closer to launch day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`bg-[#FDF4E3] border border-[#E5D8C1] ${compact ? "p-4" : "p-6"} space-y-3`} data-testid={`waitlist-form-${interest}`}>
      {headline && <div className="font-heading text-2xl uppercase tracking-wide">{headline}</div>}
      {subhead && <p className="text-sm text-[#5C5A56]">{subhead}</p>}
      {referredBy && (
        <div className="text-xs bg-[#FFB300]/20 border border-[#FFB300] px-3 py-2 text-[#5C5A56]" data-testid={`waitlist-referred-by-${interest}`}>
          Referred by <b className="font-mono">{referredBy}</b> — they&apos;ll jump the queue when you join.
        </div>
      )}
      {!compact && (
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className={input} data-testid={`waitlist-name-${interest}`} />
      )}
      <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={input} data-testid={`waitlist-email-${interest}`} />
      {!compact && (
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything you want us to know? (optional)" className={input + " resize-none"} data-testid={`waitlist-notes-${interest}`} />
      )}
      <button type="submit" disabled={loading} className="w-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white py-3 uppercase tracking-widest font-heading disabled:opacity-60 flex items-center justify-center gap-2" data-testid={`waitlist-submit-${interest}`}>
        {loading && <Loader2 className="animate-spin" size={16} />} Notify me at launch
      </button>
      <div className="text-xs text-[#5C5A56] text-center">
        Or DM <a href="https://www.tiktok.com/@alekastreatsllc" target="_blank" rel="noreferrer" className="text-[#D32F2F] font-semibold">@alekastreatsllc</a> to buy today.
      </div>
    </form>
  );
}
